import * as d3 from 'd3'

export type DateV2 = Date
export type DateV2ForApiRequest = string
type DateV2FormatPattern = 'YYYY/MM/DD' | 'YYYY-MM-DD' | 'YYMMDD' | 'm/d'

const newDateFromString = (src: string): DateV2 => {
  return new Date(src)
}

const today = (): DateV2 => {
  const now = new Date()
  now.setHours(0)
  now.setMinutes(0)
  now.setSeconds(0)
  now.setMilliseconds(0)
  return now
}

const maxDate = (): DateV2 => {
  return new Date('9999-12-31')
}

const fromApiResponseToVo = (src: string | undefined): DateV2 | undefined => {
  if (!src) return undefined
  return d3.timeDay.floor(new Date(src))
}
const fromVoToApiRequest = (src: DateV2): string => {
  return formatDate(src, 'YYYY-MM-DD')
}
const fromString = (src: string | undefined): DateV2 | undefined => {
  if (!src) return undefined
  const dateValue = new Date(src)
  if (Number.isNaN(dateValue.getTime())) return undefined
  return d3.timeDay.floor(dateValue)
}
const fromNumber = (src: number | undefined): DateV2 | undefined => {
  if (!src) return undefined
  const dateValue = new Date(src)
  if (Number.isNaN(dateValue.getTime())) return undefined
  return d3.timeDay.floor(dateValue)
}

const format = (
  src: DateV2,
  format: DateV2FormatPattern = 'YYYY/MM/DD'
): string => {
  return formatDate(src, format)
}

const formatMonthAndDate = (src: DateV2): string => {
  return formatDate(src, 'm/d')
}

const isEqual = (a: DateV2 | undefined, b: DateV2 | undefined): boolean => {
  if (!a && !b) return true
  if (!a || !b) return false
  return a.valueOf() === b.valueOf()
}

const isEqualOrBefore = (a: DateV2, b: DateV2): boolean => {
  const aDate = formatDate(a)
  const bDate = formatDate(b)
  return new Date(aDate).getTime() <= new Date(bDate).getTime()
}

const isBefore = (a: DateV2, b: DateV2): boolean => {
  return a.getTime() < b.getTime()
}

const addDays = (src: DateV2, days: number): DateV2 => {
  const result = new Date(src)
  result.setDate(result.getDate() + days)
  return d3.timeDay.floor(result)
}

/** @param month 0-indexed (0 = January, 11 = December) */
const daysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate()
}

const formatWithWeekday = (dateStr: string): string => {
  const [y, m, d] = dateStr.split('/').map(Number)
  const weekday = new Date(y, m - 1, d).toLocaleDateString('ja-JP', {
    weekday: 'short',
  })
  return `${dateStr}（${weekday}）`
}

export const dateV2Service = {
  newDateFromString,
  today,
  maxDate,
  format,
  formatMonthAndDate,
  formatWithWeekday,
  fromApiResponseToVo,
  fromVoToApiRequest,
  fromString,
  fromNumber,
  isEqual,
  isEqualOrBefore,
  isBefore,
  addDays,
  daysInMonth,
}

// Private
const formatDate = (
  src: DateV2,
  formatPattern?: DateV2FormatPattern
): string => {
  const year = src.getFullYear()
  const month = src.getMonth() + 1
  const fullMonth = month < 10 ? `0${month}` : month.toString()
  const date = src.getDate()
  const fullDate = date < 10 ? `0${date}` : date.toString()
  if ('YYYY-MM-DD' === formatPattern) {
    return `${year}-${fullMonth}-${fullDate}`
  } else if ('YYMMDD' === formatPattern) {
    const shortYear = String(year).slice(-2)
    return `${shortYear}${fullMonth}${fullDate}`
  } else if ('m/d' === formatPattern) {
    return `${month.toString()}/${date.toString()}`
  }
  return `${year}/${fullMonth}/${fullDate}`
}
